/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  FC,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useLayoutEffect,
  useCallback,
} from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useSelector } from 'react-redux'

import {
  Overlay,
  StyledForm,
  ContentWrap,
  CloseButton,
  Button,
  Title,
} from './styled'

import { InputLabel, InputError } from 'src/components/Typography'
import { CitiesInput, StyledInput } from 'src/components/Inputs'
import { CloseIcon } from 'src/components/Icons'
import { useDispatch, iRootState } from 'src/store'
import { CitySelect } from 'src/components/CitySelect'
import { City } from 'src/store/models/user'
import { updateUser, checkUsername } from 'src/api/user/user'
import { useDebounce } from 'src/hooks/useDebounce'

const stateSelector = (state: iRootState) => ({
  username: state.user.data?.username,
  firstName: state.user.data?.firstName,
  instagram: state.user.data?.instagram,
  twitter: state.user.data?.twitter,
  city: state.user.data?.city,
})

type EditProfileProps = {
  setIsLoading: Dispatch<SetStateAction<boolean>>
  setOpenEdit: Dispatch<SetStateAction<boolean>>
  openEdit: boolean
  prevCity?: City
}

const EditProfile: FC<EditProfileProps> = ({
  setIsLoading,
  setOpenEdit,
  openEdit,
  prevCity,
}) => {
  const dispatch = useDispatch()

  const { username, firstName, instagram, twitter, city } = useSelector(
    stateSelector
  )

  const [openCity, setOpenCity] = useState(false)
  const [selectedCity, setSelectedCity] = useState<City | undefined>()
  const [usernameTaken, setUsernameTaken] = useState<boolean>()
  const [usernameCheckError, setUsernameCheckError] = useState<boolean>()

  const resetCity = useCallback(() => {
    prevCity &&
      prevCity !== selectedCity &&
      dispatch.user.updateUser({ city: prevCity })
  }, [selectedCity, prevCity, dispatch])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: username || '',
      firstName: firstName || '',
      instagram: instagram || '',
      twitter: twitter || '',
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .required('Required')
        .matches(/^[A-Za-z0-9_@\-"',\s]+$/u, 'Invalid character')
        .min(2, 'Username must be at least 2 characters long.')
        .max(30, 'Too long username'),
      firstName: Yup.string()
        .required('Required')
        .matches(/^[A-Za-z0-9_@\-"',\s]+$/u, 'Invalid character')
        .min(2, 'Your name must be at least 2 characters long.')
        .max(30, 'Too long name'),
    }),
    onSubmit: async (values) => {
      const cityData = selectedCity?.state
        ? {
            name: selectedCity?.name || '',
            country: selectedCity?.country || '',
            state: selectedCity?.state || '',
          }
        : {
            name: selectedCity?.name || '',
            country: selectedCity?.country || '',
          }

      try {
        const updatedUser = await updateUser({
          ...values,
          city: cityData,
        })

        dispatch.user.setUser(updatedUser)
        setOpenEdit(false)
      } catch (error) {
        console.log(error)
      }
    },
  })

  const debouncedUsername = useDebounce(formik.values.username, 500)

  useLayoutEffect(() => {
    city && setSelectedCity(city)
  }, [city, setSelectedCity])

  useEffect(() => {
    !username && !firstName && !instagram && !twitter
      ? setIsLoading(true)
      : setIsLoading(false)
  }, [username, firstName, instagram, twitter, setIsLoading])

  useEffect(() => {
    const debouncedFetch = async () => {
      if (debouncedUsername.length > 0 && debouncedUsername !== username) {
        try {
          setUsernameTaken(false)
          setUsernameCheckError(false)
          const resp = await checkUsername(debouncedUsername)
          setUsernameTaken(resp.usernameTaken)
        } catch (error) {
          setUsernameCheckError(true)
        }
      } else {
        setUsernameTaken(false)
      }
    }

    debouncedFetch()
  }, [debouncedUsername, username])

  return (
    <Overlay isOpen={openEdit}>
      <CitySelect
        openCity={openCity}
        setOpenCity={setOpenCity}
        setSelectedCity={setSelectedCity}
      />
      <ContentWrap>
        <CloseButton
          onClick={() => {
            resetCity()
            setOpenEdit(false)
          }}
        >
          <CloseIcon />
        </CloseButton>
        <StyledForm onSubmit={formik.handleSubmit}>
          <Title>Edit profile</Title>
          <InputLabel htmlFor="username">Username</InputLabel>
          <StyledInput
            id="username"
            name="username"
            type="text"
            placeholder={username ? username : 'johndoe'}
            onChange={formik.handleChange}
            value={formik.values.username}
            onBlur={formik.handleBlur}
            isError={
              (formik.errors.username && formik.touched.username) ||
              usernameTaken ||
              usernameCheckError
                ? true
                : false
            }
          />
          {formik.errors.username && formik.touched.username && (
            <InputError>{formik.errors.username}</InputError>
          )}
          {usernameTaken && (
            <InputError>This username is already taken.</InputError>
          )}
          {usernameCheckError && (
            <InputError>Error checking username.</InputError>
          )}
          <InputLabel htmlFor="firsName">First Name</InputLabel>
          <StyledInput
            id="firstName"
            name="firstName"
            type="text"
            placeholder={firstName ? firstName : 'John'}
            onChange={formik.handleChange}
            value={formik.values.firstName}
            onBlur={formik.handleBlur}
            isError={
              formik.errors.firstName && formik.touched.firstName ? true : false
            }
          />
          {formik.errors.firstName && formik.touched.firstName && (
            <InputError>{formik.errors.firstName}</InputError>
          )}
          <InputLabel htmlFor="city">City</InputLabel>
          <CitiesInput
            preview={city ? false : true}
            onClick={() => setOpenCity(true)}
          >
            {selectedCity?.name}
          </CitiesInput>
          <InputLabel htmlFor="instagram">Instagram</InputLabel>
          <StyledInput
            id="instagram"
            name="instagram"
            type="text"
            placeholder={instagram ? instagram : 'username'}
            onChange={formik.handleChange}
            value={formik.values.instagram}
          />
          <InputLabel htmlFor="twitter">Twitter</InputLabel>
          <StyledInput
            id="twitter"
            name="twitter"
            type="text"
            placeholder={twitter ? twitter : 'username'}
            onChange={formik.handleChange}
            value={formik.values.twitter}
          />
          <Button
            type="submit"
            disabled={!formik.isValid || usernameTaken || usernameCheckError}
          >
            Save
          </Button>
        </StyledForm>
      </ContentWrap>
    </Overlay>
  )
}

export default EditProfile
